import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FileSpreadsheet, 
  Download, 
  Upload, 
  Eye, 
  Plus,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Clock,
  Building2,
  AlertCircle,
  CheckCircle2,
  Loader2,
  FileText,
  Trash2,
  Search,
  Filter,
  ArrowUpDown,
  BarChart3,
  PieChart
} from 'lucide-react';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Label } from './ui/label';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '0.00%';
  return `${(value * 100).toFixed(4)}%`;
};

// Simple pie chart component for breakdown visualization
const BreakdownChart = ({ labor, odcs, subsAndMaterials }) => {
  const total = labor + odcs + subsAndMaterials;
  if (total === 0) return null;
  
  const laborPct = (labor / total) * 100;
  const odcsPct = (odcs / total) * 100;
  const subsPct = (subsAndMaterials / total) * 100;
  
  return (
    <div className="flex items-center gap-4">
      <div className="flex-1">
        <div className="h-4 rounded-full overflow-hidden flex bg-gray-200">
          <div 
            className="bg-blue-500 h-full" 
            style={{ width: `${laborPct}%` }}
            title={`Labor: ${laborPct.toFixed(1)}%`}
          />
          <div 
            className="bg-orange-500 h-full" 
            style={{ width: `${odcsPct}%` }}
            title={`ODCs: ${odcsPct.toFixed(1)}%`}
          />
          <div 
            className="bg-green-500 h-full" 
            style={{ width: `${subsPct}%` }}
            title={`Subs & Materials: ${subsPct.toFixed(1)}%`}
          />
        </div>
        <div className="flex justify-between text-xs mt-1 text-gray-600">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            Labor {laborPct.toFixed(1)}%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
            ODCs {odcsPct.toFixed(1)}%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Subs/Mat {subsPct.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};

const ProjectCard = ({ project, isExpanded, onToggle, includeMaterialHandling = true }) => {
  return (
    <Card className="mb-4 overflow-hidden" data-testid={`project-card-${project.project_number}`}>
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                {project.project_number}
              </CardTitle>
              {project.prime_contract_no && (
                <p className="text-xs text-gray-500 mt-0.5">Prime: {project.prime_contract_no}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-xs text-gray-500">Labor</p>
              <p className="font-semibold text-blue-600 text-sm">{formatCurrency(project.subtotal_labor)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Other Costs</p>
              <p className="font-semibold text-orange-600 text-sm">{formatCurrency(project.subtotal_other_subs_materials)}</p>
            </div>
            <div className="text-right min-w-[100px]">
              <p className="text-xs text-gray-500">Total</p>
              <p className="font-bold text-base">{formatCurrency(project.total)}</p>
            </div>
          </div>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="border-t pt-4">
          {/* Labor Section */}
          <div className="mb-6">
            <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4" />
              PROJECT LABOR CATEGORY
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-800 text-white">
                    <th className="text-left p-2">Labor Category</th>
                    <th className="text-right p-2">Rate</th>
                    <th className="text-right p-2">Hours</th>
                    <th className="text-right p-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {project.labor_items && project.labor_items.length > 0 ? (
                    project.labor_items.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="p-2">{item.labor_category}</td>
                        <td className="text-right p-2">{formatCurrency(item.bill_rate)}</td>
                        <td className="text-right p-2">{formatNumber(item.hours)}</td>
                        <td className="text-right p-2 font-medium">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="p-4 text-center text-gray-500">
                        No labor data available for this project
                      </td>
                    </tr>
                  )}
                  <tr className="bg-blue-100 font-semibold">
                    <td colSpan={3} className="p-2">Subtotal - Labor</td>
                    <td className="text-right p-2">{formatCurrency(project.subtotal_labor)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Other Costs Section */}
          <div className="mb-6">
            <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2 text-sm">
              <DollarSign className="h-4 w-4" />
              OTHER COSTS
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="bg-gray-50">
                    <td className="p-2">ODCs</td>
                    <td className="text-right p-2">{formatCurrency(project.odcs)}</td>
                  </tr>
                  <tr>
                    <td className="p-2">Unbillable ODCs</td>
                    <td className="text-right p-2">{formatCurrency(project.unbillable_odcs)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="p-2">UNBURDENED ODCs (No G&A)</td>
                    <td className="text-right p-2">{formatCurrency(project.unburdened_odcs_no_ga)}</td>
                  </tr>
                  <tr className="bg-orange-100 font-semibold">
                    <td className="p-2">Subtotal - Other Costs</td>
                    <td className="text-right p-2">{formatCurrency(project.subtotal_other_costs)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="p-2">G&A</td>
                    <td className="text-right p-2">{formatCurrency(project.ga)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Subs & Materials Section */}
          <div className="mb-6">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2 text-sm">
              <FileSpreadsheet className="h-4 w-4" />
              SUBS & MATERIALS COSTS
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="bg-gray-50">
                    <td className="p-2">SUB ODCs, Pos, and Sub Labor as ODC</td>
                    <td className="text-right p-2">{formatCurrency(project.sub_odcs_labor_as_odc)}</td>
                  </tr>
                  <tr>
                    <td className="p-2">SUB ODCs - UNBILLABLE</td>
                    <td className="text-right p-2">{formatCurrency(project.sub_odcs_unbillable)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="p-2">Materials</td>
                    <td className="text-right p-2">{formatCurrency(project.materials)}</td>
                  </tr>
                  <tr>
                    <td className="p-2">CIO-CS-CAF (ODC applied with discounted G&A)</td>
                    <td className="text-right p-2">{formatCurrency(project.cio_cs_caf)}</td>
                  </tr>
                  <tr className="bg-green-100 font-semibold">
                    <td className="p-2">Subtotal - Subs and Materials Costs</td>
                    <td className="text-right p-2">{formatCurrency(project.subtotal_subs_materials)}</td>
                  </tr>
                  {includeMaterialHandling && (
                    <tr className="bg-gray-50">
                      <td className="p-2">SM&H</td>
                      <td className="text-right p-2">{formatCurrency(project.smh)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Final Totals */}
          <div className="border-t pt-4">
            <table className="w-full text-sm">
              <tbody>
                <tr className="bg-gray-200 font-semibold">
                  <td className="p-2">Subtotal - Other, Subs, & Materials</td>
                  <td className="text-right p-2">{formatCurrency(project.subtotal_other_subs_materials)}</td>
                </tr>
                <tr className="bg-gray-800 text-white font-bold text-base">
                  <td className="p-3">TOTAL</td>
                  <td className="text-right p-3">{formatCurrency(project.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const ScheduleKPage = () => {
  const { clientId, clientName, hasActiveClient } = useClient();
  const [scheduleHList, setScheduleHList] = useState([]);
  const [selectedScheduleH, setSelectedScheduleH] = useState('');
  const [scheduleKList, setScheduleKList] = useState([]);
  const [selectedScheduleK, setSelectedScheduleK] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [projectFilter, setProjectFilter] = useState('');
  const [laborDataInfo, setLaborDataInfo] = useState(null);
  const [viewMode, setViewMode] = useState('single');
  
  // New state for enhanced features
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('project_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [excludeZeroCost, setExcludeZeroCost] = useState(true);
  const [excludeZeroLabor, setExcludeZeroLabor] = useState(false);
  const [contractTypeFilter, setContractTypeFilter] = useState('all');
  const [showCharts, setShowCharts] = useState(true);
  const [quickJumpProject, setQuickJumpProject] = useState('');

  // Load Schedule H list
  const loadScheduleHList = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/schedule-h/list?limit=20&tenant_id=${clientId}`);
      if (res.ok) {
        const data = await res.json();
        const calculations = data.calculations || [];
        setScheduleHList(calculations);
        if (!selectedScheduleH && calculations.length > 0) {
          setSelectedScheduleH(calculations[0].schedule_h_id);
        }
      }
    } catch (e) {
      console.error('Error loading Schedule H list:', e);
    }
  }, [selectedScheduleH, clientId]);

  // Load Schedule K list
  const loadScheduleKList = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/schedule-k/list?tenant_id=${clientId}`);
      if (res.ok) {
        const data = await res.json();
        setScheduleKList(data.schedule_k || []);
      }
    } catch (e) {
      console.error('Error loading Schedule K list:', e);
    }
  }, [clientId]);

  useEffect(() => {
    loadScheduleHList();
    loadScheduleKList();
  }, [loadScheduleHList, loadScheduleKList, clientId]);

  // Load preview when Schedule H changes
  const loadPreview = useCallback(async () => {
    if (!selectedScheduleH) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API}/schedule-k/preview?schedule_h_id=${encodeURIComponent(selectedScheduleH)}`;
      url += `&exclude_zero_cost=${excludeZeroCost}`;
      url += `&exclude_zero_labor=${excludeZeroLabor}`;
      url += `&use_full_project_number=true`;
      if (projectFilter) {
        url += `&project_filter=${encodeURIComponent(projectFilter)}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
        setLaborDataInfo({
          hasData: data.has_labor_data,
          entryCount: data.labor_entry_count
        });
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to load preview');
      }
    } catch (e) {
      setError('Error loading preview: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedScheduleH, projectFilter, excludeZeroCost, excludeZeroLabor]);

  useEffect(() => {
    if (selectedScheduleH) {
      loadPreview();
    }
  }, [selectedScheduleH, loadPreview]);

  // Handle T&M Labor file upload
  const handleLaborUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedScheduleH) return;
    
    setUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schedule_h_id', selectedScheduleH);
      
      const res = await fetch(`${API}/schedule-k/upload-labor`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setSuccess(`Uploaded ${data.entry_count} labor entries from ${data.filename}`);
        setLaborDataInfo({
          hasData: true,
          entryCount: data.entry_count
        });
        loadPreview();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to upload labor data');
      }
    } catch (e) {
      setError('Error uploading file: ' + e.message);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // Generate Schedule K
  const handleGenerate = async () => {
    if (!selectedScheduleH) return;
    
    setGenerating(true);
    setError(null);
    setSuccess(null);
    
    try {
      const res = await fetch(`${API}/schedule-k/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schedule_h_id: selectedScheduleH,
          project_filter: projectFilter || null,
          exclude_zero_cost: excludeZeroCost,
          exclude_zero_labor: excludeZeroLabor
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedScheduleK(data);
        setSuccess(`Generated Schedule K with ${data.project_count} projects. Total: ${formatCurrency(data.total_amount)}`);
        loadScheduleKList();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to generate Schedule K');
      }
    } catch (e) {
      setError('Error generating Schedule K: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  // Load existing Schedule K
  const loadScheduleK = async (scheduleKId) => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/schedule-k/${scheduleKId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedScheduleK(data);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to load Schedule K');
      }
    } catch (e) {
      setError('Error loading Schedule K: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Export Schedule K
  const handleExport = async (format = 'xlsx') => {
    if (!selectedScheduleK?.id) return;
    
    try {
      const res = await fetch(`${API}/schedule-k/${selectedScheduleK.id}/export?format=${format}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Schedule_K_${selectedScheduleK.id.slice(0, 8)}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to export Schedule K');
      }
    } catch (e) {
      setError('Error exporting: ' + e.message);
    }
  };

  // Delete Schedule K
  const handleDelete = async (scheduleKId) => {
    if (!window.confirm('Are you sure you want to delete this Schedule K?')) return;
    
    try {
      const res = await fetch(`${API}/schedule-k/${scheduleKId}`, { method: 'DELETE' });
      if (res.ok) {
        setSuccess('Schedule K deleted');
        if (selectedScheduleK?.id === scheduleKId) {
          setSelectedScheduleK(null);
        }
        loadScheduleKList();
      } else {
        setError('Failed to delete Schedule K');
      }
    } catch (e) {
      setError('Error deleting: ' + e.message);
    }
  };

  // Toggle project expansion
  const toggleProject = (projectNumber) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectNumber]: !prev[projectNumber]
    }));
  };

  // Expand/collapse all
  const expandAll = () => {
    const projects = selectedScheduleK?.projects || previewData?.projects || [];
    const newExpanded = {};
    projects.forEach(p => { newExpanded[p.project_number] = true; });
    setExpandedProjects(newExpanded);
  };

  const collapseAll = () => {
    setExpandedProjects({});
  };

  // Quick jump to project
  const handleQuickJump = (projectNumber) => {
    setQuickJumpProject(projectNumber);
    setExpandedProjects(prev => ({ ...prev, [projectNumber]: true }));
    // Scroll to project
    setTimeout(() => {
      const element = document.querySelector(`[data-testid="project-card-${projectNumber}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Sort and filter projects
  const displayData = selectedScheduleK || previewData;
  const includeMaterialHandling = displayData?.layout?.include_material_handling !== false;
  const sortedAndFilteredProjects = useMemo(() => {
    let projects = displayData?.projects || [];
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      projects = projects.filter(p => 
        p.project_number.toLowerCase().includes(query) ||
        (p.prime_contract_no && p.prime_contract_no.toLowerCase().includes(query))
      );
    }
    
    // Filter by $0 total cost
    if (excludeZeroCost) {
      projects = projects.filter(p => p.total !== 0);
    }
    
    // Filter by $0 labor
    if (excludeZeroLabor) {
      projects = projects.filter(p => p.subtotal_labor !== 0);
    }
    
    // Filter by contract type
    if (contractTypeFilter && contractTypeFilter !== 'all') {
      projects = projects.filter(p => {
        const contractType = p.contract_type || '';
        return contractType.toLowerCase() === contractTypeFilter.toLowerCase();
      });
    }
    
    // Sort
    projects = [...projects].sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'total':
          aVal = a.total;
          bVal = b.total;
          break;
        case 'labor':
          aVal = a.subtotal_labor;
          bVal = b.subtotal_labor;
          break;
        case 'other_costs':
          aVal = a.subtotal_other_subs_materials;
          bVal = b.subtotal_other_subs_materials;
          break;
        default:
          aVal = a.project_number;
          bVal = b.project_number;
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return projects;
  }, [displayData, searchQuery, sortBy, sortOrder, excludeZeroCost, excludeZeroLabor, contractTypeFilter]);

  // Get unique contract types from the data
  const availableContractTypes = useMemo(() => {
    const projects = displayData?.projects || [];
    const types = new Set();
    projects.forEach(p => {
      if (p.contract_type) {
        types.add(p.contract_type);
      }
    });
    return Array.from(types).sort();
  }, [displayData]);

  // Project list for quick jump
  const projectList = useMemo(() => {
    return (displayData?.projects || []).map(p => p.project_number);
  }, [displayData]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="schedule-k-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileSpreadsheet className="h-7 w-7 text-blue-600" />
          Schedule K - T&M/Labor Hour Contracts
        </h1>
        <p className="text-gray-600 mt-1">
          Summary of Hours and Amounts on Time & Materials contracts
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4" data-testid="error-alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
          <button onClick={() => setError(null)} className="ml-auto text-lg">×</button>
        </Alert>
      )}
      
      {success && (
        <Alert className="mb-4 bg-green-50 border-green-200" data-testid="success-alert">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
          <button onClick={() => setSuccess(null)} className="ml-auto text-lg">×</button>
        </Alert>
      )}

      {/* Sticky Rate Cards */}
      {displayData?.rates && (
        <div className="sticky top-0 z-10 bg-white pb-4 -mx-6 px-6 border-b shadow-sm mb-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-2">
            <Card className="bg-blue-50">
              <CardContent className="p-3">
                <p className="text-xs text-gray-500">G&A Rate</p>
                <p className="text-lg font-bold text-blue-600">{formatPercent(displayData.rates.ga)}</p>
              </CardContent>
            </Card>
            {includeMaterialHandling && (
              <Card className="bg-green-50">
                <CardContent className="p-3">
                  <p className="text-xs text-gray-500">SM&H Rate</p>
                  <p className="text-lg font-bold text-green-600">{formatPercent(displayData.rates.smh)}</p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-gray-500">Projects</p>
                <p className="text-lg font-bold">{sortedAndFilteredProjects.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-gray-500">Labor Data</p>
                <p className="text-lg font-bold">{laborDataInfo?.hasData ? `${laborDataInfo.entryCount} entries` : 'None'}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-100">
              <CardContent className="p-3">
                <p className="text-xs text-gray-500">Grand Total</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(displayData.consolidated?.grand_total || displayData.total_amount)}
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Charts */}
          {showCharts && displayData?.consolidated && (
            <div className="mt-3">
              <BreakdownChart 
                labor={displayData.consolidated.total_labor || 0}
                odcs={displayData.consolidated.total_odcs || 0}
                subsAndMaterials={displayData.consolidated.total_subs_materials || 0}
              />
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Schedule H Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schedule H Source
              </label>
              <Select value={selectedScheduleH} onValueChange={setSelectedScheduleH}>
                <SelectTrigger data-testid="schedule-h-select">
                  <SelectValue placeholder="Select Schedule H" />
                </SelectTrigger>
                <SelectContent>
                  {scheduleHList.map(sh => (
                    <SelectItem key={sh.schedule_h_id} value={sh.schedule_h_id}>
                      {sh.schedule_h_id.slice(0, 8)}... ({sh.contract_count} contracts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Prefix Filter
              </label>
              <Input
                placeholder="e.g., 20825"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                data-testid="project-filter-input"
              />
            </div>

            {/* T&M Labor Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                T&M Labor Data
              </label>
              <div className="flex items-center gap-2">
                <label className="flex-1">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleLaborUpload}
                    className="hidden"
                    disabled={!selectedScheduleH || uploading}
                    data-testid="labor-upload-input"
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={!selectedScheduleH || uploading}
                    asChild
                  >
                    <span className="cursor-pointer">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                      {uploading ? 'Uploading...' : 'Upload Labor'}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Quick Jump */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quick Jump to Project
              </label>
              <Select value={quickJumpProject} onValueChange={handleQuickJump}>
                <SelectTrigger data-testid="quick-jump-select">
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {projectList.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Second row - Search, Sort, Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Projects
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by project number or prime contract..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <div className="flex gap-2">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="sort-select" className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project_number">Project Number</SelectItem>
                    <SelectItem value="total">Total Amount</SelectItem>
                    <SelectItem value="labor">Labor Amount</SelectItem>
                    <SelectItem value="other_costs">Other Costs</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Toggle Options */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Switch
                  id="exclude-zero"
                  checked={excludeZeroCost}
                  onCheckedChange={(checked) => {
                    setExcludeZeroCost(checked);
                  }}
                />
                <Label htmlFor="exclude-zero" className="text-sm">Hide $0 projects</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="exclude-zero-labor"
                  checked={excludeZeroLabor}
                  onCheckedChange={(checked) => {
                    setExcludeZeroLabor(checked);
                  }}
                />
                <Label htmlFor="exclude-zero-labor" className="text-sm">Hide $0 labor projects</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-charts"
                  checked={showCharts}
                  onCheckedChange={setShowCharts}
                />
                <Label htmlFor="show-charts" className="text-sm">Show charts</Label>
              </div>
            </div>

            {/* Contract Type Filter */}
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Contract Type</Label>
              <Select value={contractTypeFilter} onValueChange={setContractTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {availableContractTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t">
            <Button
              onClick={loadPreview}
              variant="outline"
              disabled={!selectedScheduleH || loading}
              data-testid="preview-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              Preview
            </Button>
            
            <Button
              onClick={handleGenerate}
              disabled={!selectedScheduleH || generating}
              data-testid="generate-btn"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Generate Schedule K
            </Button>
            
            {selectedScheduleK && (
              <>
                <Button
                  onClick={() => handleExport('xlsx')}
                  variant="secondary"
                  data-testid="export-xlsx-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Excel
                </Button>
                <Button
                  onClick={() => handleExport('csv')}
                  variant="outline"
                  data-testid="export-csv-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </>
            )}
            
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={expandAll}>
                Expand All
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAll}>
                Collapse All
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Saved Schedule K List */}
      {scheduleKList.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Saved Schedule K Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {scheduleKList.map(sk => (
                <div 
                  key={sk.id} 
                  className={`flex items-center justify-between p-3 rounded border cursor-pointer hover:bg-gray-50 ${
                    selectedScheduleK?.id === sk.id ? 'border-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => loadScheduleK(sk.id)}
                  data-testid={`schedule-k-item-${sk.id}`}
                >
                  <div>
                    <p className="font-medium text-sm">{sk.id.slice(0, 8)}...</p>
                    <p className="text-xs text-gray-500">
                      {new Date(sk.created_at).toLocaleString()} • {sk.project_count} projects
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{formatCurrency(sk.total_amount)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(sk.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consolidated View */}
      {viewMode === 'consolidated' && displayData?.consolidated && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Consolidated Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b">
                  <td className="py-2 font-medium">Total Labor</td>
                  <td className="py-2 text-right font-semibold text-blue-600">
                    {formatCurrency(displayData.consolidated.total_labor)}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Total ODCs</td>
                  <td className="py-2 text-right">{formatCurrency(displayData.consolidated.total_odcs)}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Total Unbillable ODCs</td>
                  <td className="py-2 text-right">{formatCurrency(displayData.consolidated.total_unbillable_odcs)}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Total Subs & Materials</td>
                  <td className="py-2 text-right">{formatCurrency(displayData.consolidated.total_subs_materials)}</td>
                </tr>
                {includeMaterialHandling && (
                  <tr className="border-b">
                    <td className="py-2 font-medium">Total SM&H</td>
                    <td className="py-2 text-right text-green-600">{formatCurrency(displayData.consolidated.total_smh)}</td>
                  </tr>
                )}
                <tr className="border-b">
                  <td className="py-2 font-medium">Total G&A</td>
                  <td className="py-2 text-right text-orange-600">{formatCurrency(displayData.consolidated.total_ga)}</td>
                </tr>
                <tr className="bg-gray-100">
                  <td className="py-3 font-bold text-lg">GRAND TOTAL</td>
                  <td className="py-3 text-right font-bold text-lg">{formatCurrency(displayData.consolidated.grand_total)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Projects */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      ) : sortedAndFilteredProjects.length > 0 ? (
        <div data-testid="projects-list">
          <div className="mb-3 text-sm text-gray-500">
            Showing {sortedAndFilteredProjects.length} project{sortedAndFilteredProjects.length !== 1 ? 's' : ''}
            {searchQuery && ` matching "${searchQuery}"`}
          </div>
          {sortedAndFilteredProjects.map(project => (
            <ProjectCard
              key={project.project_number}
              project={project}
              isExpanded={expandedProjects[project.project_number]}
              onToggle={() => toggleProject(project.project_number)}
              includeMaterialHandling={includeMaterialHandling}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileSpreadsheet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedule K Data</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery 
                ? `No projects found matching "${searchQuery}"`
                : 'Select a Schedule H source and upload T&M Labor data to generate Schedule K'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScheduleKPage;
